/**
 * Availability, compressed to what a guest's browser actually needs.
 *
 * ---------------------------------------------------------------------------
 * ONE COPY, THREE CONSUMERS
 * ---------------------------------------------------------------------------
 *
 * The API computes these periods, the guest site tests them as the visitor
 * moves through a date picker, and the operator's planning board uses the same
 * functions to show what a block would cost before it is saved. Three places,
 * one implementation — a second copy written in a hurry for the frontend is
 * how a booking site starts offering dates the park cannot honour.
 *
 * Hence: no imports, no framework, no I/O. Plain TypeScript that runs anywhere
 * a `<script>` or a `require` does.
 *
 * ---------------------------------------------------------------------------
 * THE ONE RULE
 * ---------------------------------------------------------------------------
 *
 * A stay must be housed in ONE unit for all of its nights — a park cannot move
 * a family to a different pitch on the Wednesday. So a stay `[s, e)` is
 * possible exactly when some unit is free for every night in it, and it is
 * impossible exactly when every unit is blocked on at least one of those
 * nights.
 *
 * That second sentence is the useful one. It says an impossible stay is one
 * that CONTAINS a window hitting every unit. So the whole availability picture
 * for a type is the set of MINIMAL such windows, and the client's test is:
 *
 *     the stay is possible  <=>  it contains none of these windows
 *
 * ---------------------------------------------------------------------------
 * WHY THIS IS ONE KIND OF PERIOD AND NOT TWO
 * ---------------------------------------------------------------------------
 *
 * The obvious model has two: nights where everything is full, and wider
 * stretches where the units are free at different times so no single one spans
 * the stretch. They behave differently — a booking may not touch the first, but
 * may sit inside the second — and that difference looks fundamental.
 *
 * It is not. A fully-booked night is a minimal window one night long, and a
 * stay overlapping it necessarily contains it. Both cases are "does the stay
 * contain a window", and collapsing them removes the client's need to know
 * which kind it is holding, along with every rule about how the two may
 * overlap. Those rules were real, but they are consequences of minimality
 * rather than things anyone has to enforce.
 *
 * ---------------------------------------------------------------------------
 * WHAT IT COSTS AND WHAT IT DISCLOSES
 * ---------------------------------------------------------------------------
 *
 * The window count is bounded by the number of NIGHTS, never by the number of
 * units, which is what makes this worth sending at all. Measured over a
 * 60-night horizon at three occupancy levels:
 *
 *     units      20% busy   50% busy   80% busy   mean length @50%
 *         1          12.8       30.4       49.9        1.0 nights
 *         5           9.5       24.5       38.4        2.7 nights
 *       100           6.9       22.3       37.1        6.9 nights
 *
 * More units does not mean far fewer windows — an earlier version of this
 * comment claimed it did, and the measurement says otherwise. What actually
 * happens is that the windows get LONGER, because one has to hit every unit to
 * exist: a hundred-pitch type at half occupancy needs about seven nights to
 * catch everybody. The count barely moves; the shape does.
 *
 * The payload therefore scales with the season length and the occupancy, and a
 * hundred-pitch type costs about the same to describe as a five-pitch one —
 * where per-unit calendars would cost twenty times more.
 *
 * On disclosure: this hands over the feasibility of every possible stay at
 * once, which is more than answering one question at a time. But a booking
 * site has to reveal exactly that much or it cannot take bookings, and what it
 * withholds is the part that is nobody's business — WHICH pitch is free, and
 * therefore how full the park is on any given night.
 */

export interface Blocked {
  unitId: string;
  /** First blocked night. */
  from: number;
  /** Night after the last blocked one. */
  until: number;
}

export interface Window {
  from: number;
  until: number;
}

/**
 * Minimal windows that hit every unit at least once.
 *
 * A sliding window over the nights: widen until every unit has been touched,
 * then narrow from the left for as long as that stays true, and what is left
 * is minimal. Emitting only the narrowest window for each right-hand edge is
 * what keeps the result an antichain — no window contains another, so none of
 * them is redundant.
 */
export function blockingWindows(
  unitIds: readonly string[], blocks: readonly Blocked[], from: number, until: number,
): Window[] {
  if (until <= from) return [];

  // Nights each unit is blocked on, as a lookup per night.
  //
  // The ids are deduplicated, and `total` counts DISTINCT units rather than
  // rows. A repeated id — a join that fanned out, two queries concatenated —
  // otherwise raises the coverage target above the number of slots the map
  // has, coverage never completes, and a type that may be completely full
  // reports no windows at all.
  const index = new Map<string, number>();
  for (const id of unitIds) if (!index.has(id)) index.set(id, index.size);
  const total = index.size;

  // A type with no units houses nothing, so every night of the horizon is a
  // closure. Returning [] here reads as "nothing is ever blocked" and offers
  // the whole season for a type with no pitches — and because `combineWindows`
  // treats a type with no windows as free throughout, one such type whitewashes
  // every other type in a filtered calendar. This is the same convention
  // `combineWindows` already applies to an empty SET of types.
  if (total === 0) {
    return Array.from({ length: until - from }, (_, i) => ({ from: from + i, until: from + i + 1 }));
  }

  const perNight: number[][] = Array.from({ length: until - from }, () => []);
  for (const b of blocks) {
    const i = index.get(b.unitId);
    if (i === undefined) continue;
    const lo = Math.max(from, b.from);
    const hi = Math.min(until, b.until);
    for (let n = lo; n < hi; n++) perNight[n - from].push(i);
  }

  const seen = new Int32Array(total);
  let covered = 0;
  const out: Window[] = [];
  let lo = from;

  for (let hi = from; hi < until; hi++) {
    for (const u of perNight[hi - from]) {
      if (seen[u] === 0) covered++;
      seen[u]++;
    }
    if (covered < total) continue;

    // Every unit is hit somewhere in [lo, hi]. Pull `lo` up as far as it goes.
    while (covered === total) {
      for (const u of perNight[lo - from]) {
        seen[u]--;
        if (seen[u] === 0) covered--;
      }
      lo++;
    }
    // `lo` has gone one night too far; the window that was minimal ends here.
    lo--;
    for (const u of perNight[lo - from]) {
      if (seen[u] === 0) covered++;
      seen[u]++;
    }

    const w = { from: lo, until: hi + 1 };
    // Same right edge as the previous window means this one is narrower, so it
    // replaces it rather than joining it.
    if (out.length && out[out.length - 1].until === w.until) out[out.length - 1] = w;
    else out.push(w);

    // Move past this window's left edge; anything starting at or before `lo`
    // is already covered by it.
    for (const u of perNight[lo - from]) {
      seen[u]--;
      if (seen[u] === 0) covered--;
    }
    lo++;
  }

  return out;
}

/**
 * Can this stay be housed?
 *
 * The client runs the same test, which is the point — one download answers
 * every date and length the guest tries, with no round trip.
 */
export function stayPossible(windows: readonly Window[], from: number, nights: number): boolean {
  const until = from + nights;
  for (const w of windows) if (w.from >= from && w.until <= until) return false;
  return true;
}

// ---------------------------------------------------------------------------
// The form that goes over the wire
// ---------------------------------------------------------------------------

/**
 * Minimal windows split into the two shapes a client tests differently.
 *
 * Computing one primitive and transmitting two is deliberate. Minimality is
 * what makes the encoding correct, and it forces a fully-booked fortnight into
 * fourteen separate one-night windows — correct, and a silly thing to send. So
 * consecutive one-night windows are merged into a `closed` period, and because
 * every night inside one is fully booked, a stay merely TOUCHING it is already
 * impossible. That is why the two shapes carry different tests:
 *
 *     closed      rejected when the stay OVERLAPS it
 *     switching   rejected only when the stay CONTAINS it
 *
 * A stay may sit inside a switching window, or straddle either end of it, and
 * still be housed — the switch has not been forced yet. Testing those for
 * overlap would refuse bookings the park can perfectly well take.
 *
 * Correctness is inherited rather than re-argued: merging only ever replaces a
 * run of one-night windows with a period that rejects exactly the stays those
 * windows rejected.
 */
export interface Periods {
  /** Nothing is free. No overlap at all. */
  closed: Window[];
  /** Units are free at different times; a stay may not span the whole window. */
  switching: Window[];
}

export function toPeriods(windows: readonly Window[]): Periods {
  const closed: Window[] = [];
  const switching: Window[] = [];
  for (const w of windows) {
    if (w.until - w.from > 1) { switching.push({ ...w }); continue; }
    const last = closed[closed.length - 1];
    // Adjacent single nights are one closure. `until === from` rather than
    // `+1` because the windows are half-open and already touch.
    if (last && last.until === w.from) last.until = w.until;
    else closed.push({ ...w });
  }
  return { closed, switching };
}

/** The test a client runs. Two predicates, one per shape. */
export function stayPossibleFromPeriods(p: Periods, from: number, nights: number): boolean {
  const until = from + nights;
  for (const w of p.closed) if (from < w.until && until > w.from) return false;
  for (const w of p.switching) if (from <= w.from && until >= w.until) return false;
  return true;
}

// ---------------------------------------------------------------------------
// Combining several types into one answer
// ---------------------------------------------------------------------------

/**
 * Availability of a SET of types — "is anything the guest filtered for free?"
 *
 * The encoding is closed under this, which is the property that makes a
 * filterable calendar possible without a round trip. A stay is possible across
 * a set when it is possible in at least one type, so it is impossible when it
 * is impossible in every one — and "impossible in type T" already means
 * "contains one of T's windows". That family is upward-closed (widening a stay
 * cannot make it possible), and an upward-closed family of intervals is
 * exactly described by its minimal members. So the combination is again a set
 * of minimal windows, testable by the same rule.
 *
 * The sweep is the same shape one level up: instead of covering every unit, a
 * window must cover every TYPE, and a type is covered once the window contains
 * one of its windows. A closed night covers its type the moment it is reached;
 * a switching window only covers it once its far end is passed, which is the
 * "waiting on a close" the sketch describes.
 *
 * With no types at all — a filter that matches nothing — every stay is
 * impossible, and the honest encoding of that is a closed night for every
 * night. Callers normally short-circuit that case with a message rather than a
 * calendar, but the function must not lie about it.
 */
export function combineWindows(
  sets: ReadonlyArray<readonly Window[]>, from: number, until: number,
): Window[] {
  if (until <= from) return [];
  if (sets.length === 0) {
    return Array.from({ length: until - from }, (_, i) => ({ from: from + i, until: from + i + 1 }));
  }
  // A type with no windows is free somewhere in every stay, so nothing is ever
  // impossible across a set containing it.
  if (sets.some((s) => s.length === 0)) return [];

  const sorted = sets.map((s) => [...s].sort((a, b) => a.from - b.from || a.until - b.until));
  const out: Window[] = [];

  for (let s = from; s < until; s++) {
    let need = from;
    let reachable = true;
    for (const set of sorted) {
      // The soonest this type can be covered by a stay starting at `s`.
      let best = Infinity;
      for (const w of set) {
        if (w.from < s) continue;
        if (w.until < best) best = w.until;
        // Sorted by `from`, so once a window starts beyond the running best
        // there is nothing shorter left to find.
        if (w.from >= best) break;
      }
      if (best === Infinity) { reachable = false; break; }
      if (best > need) need = best;
    }
    if (!reachable || need > until) continue;
    const w = { from: s, until: need };
    // Later starts with the same end are narrower, so they replace rather than
    // accumulate — this is what keeps the result an antichain.
    if (out.length && out[out.length - 1].until === w.until) out[out.length - 1] = w;
    else out.push(w);
  }
  return out;
}

/**
 * Combine straight from the compressed sets, without expanding them first.
 *
 * The wire format is what a browser actually holds, so this is the entry point
 * a frontend calls when a filter changes. It never rebuilds the per-night
 * windows: a closed period covers its type at the FIRST of its nights the stay
 * reaches, and a switching window only once its far end is passed. Those two
 * facts are enough to run the same sweep.
 *
 * The distinction matters more than it looks. A closed period spanning nights
 * 9 and 10 does not push a combined window out to 11 — touching night 9 has
 * already made that type impossible, so the window closes at 10. Expanding and
 * re-compressing would get the same answer; doing it directly makes it obvious
 * why, and costs nothing.
 */
export function combinePeriods(sets: readonly Periods[], from: number, until: number): Periods {
  if (until <= from) return { closed: [], switching: [] };
  if (sets.length === 0) {
    return {
      closed: [{ from, until }],
      switching: [],
    };
  }
  // A type nothing blocks absorbs every stay, so nothing is ever impossible.
  if (sets.some((p) => p.closed.length === 0 && p.switching.length === 0)) {
    return { closed: [], switching: [] };
  }

  /** Soonest night by which a stay starting at `s` has made this type impossible. */
  function coverBy(p: Periods, s: number): number {
    let best = Infinity;
    for (const w of p.closed) {
      // The first night of the closure at or after `s`.
      const night = Math.max(s, w.from);
      if (night < w.until && night + 1 < best) best = night + 1;
    }
    for (const w of p.switching) {
      if (w.from >= s && w.until < best) best = w.until;
    }
    return best;
  }

  const out: Window[] = [];
  for (let s = from; s < until; s++) {
    let need = from;
    let reachable = true;
    for (const p of sets) {
      const by = coverBy(p, s);
      if (by === Infinity) { reachable = false; break; }
      if (by > need) need = by;
    }
    if (!reachable || need > until) continue;
    const w = { from: s, until: need };
    if (out.length && out[out.length - 1].until === w.until) out[out.length - 1] = w;
    else out.push(w);
  }
  return toPeriods(out);
}

// ---------------------------------------------------------------------------
// Arrival and departure restrictions
// ---------------------------------------------------------------------------

/**
 * Restrictions are properties of DATES, tested only at a stay's two ends.
 *
 * This is the market's model, not an invention: every channel manager states
 * closed-to-arrival, closed-to-departure, minimum and maximum stay per DATE,
 * and evaluates arrival rules on the arrival date and departure rules on the
 * departure date. Nothing looks at the dates in between.
 *
 * It is worth spelling out because the "obvious" alternative — a restriction
 * applying to any stay that OVERLAPS its period — gives different and worse
 * answers, and the three cases that distinguish them are exactly the ones an
 * operator asks about:
 *
 *   arriving before a restricted summer and leaving inside it
 *       the arrival date is unrestricted, so only the departure rule bites
 *   arriving inside a restricted period and leaving after it
 *       the arrival rule bites; the departure date is unrestricted, so any
 *       departure day is fine
 *   a stay long enough to contain the restricted period entirely
 *       neither end falls inside it, so nothing applies
 *
 * All three fall out of "test the ends" without a single special case, and all
 * three match what a channel would do with the same ARI feed — which matters
 * more than elegance, because a park whose own site refuses a booking that
 * Booking.com accepts has a problem no amount of internal consistency fixes.
 */
export interface Restriction {
  /** Dates this rule governs, half-open. */
  from: number;
  until: number;
  /** Weekdays a stay may begin on. 0 = Sunday. Absent means any. */
  arriveOn?: readonly number[];
  /** Weekdays a stay may end on. Absent means any. */
  departOn?: readonly number[];
  minNights?: number;
  maxNights?: number;
  /**
   * Which sales channels this rule governs.
   *
   * Absent means all of them. A park routinely closes arrivals on its own site
   * while leaving an OTA open, or the reverse, and a rule that cannot say
   * which channel it belongs to becomes a rule nobody dares change.
   */
  channels?: readonly string[];
}

/** Day-of-week for a day number. 1970-01-01 was a Thursday. */
export function weekdayOf(day: number): number {
  return (((day + 4) % 7) + 7) % 7;
}

function governs(r: Restriction, channel: string | undefined): boolean {
  return !r.channels || channel === undefined || r.channels.includes(channel);
}

/** Why a stay was refused, or null when it was not. */
export type Refusal =
  | 'closed-to-arrival' | 'closed-to-departure' | 'too-short' | 'too-long'
  /** The window is shut outright, and this stay uses a night inside it. */
  | 'closed';

export function checkRestrictions(
  rules: readonly Restriction[], from: number, nights: number, channel?: string,
): Refusal | null {
  const departure = from + nights;
  for (const r of rules) {
    if (!governs(r, channel)) continue;

    /**
     * A rule that states NO condition closes its window outright.
     *
     * DEFECT: this fell through every branch below and refused nothing. Each
     * branch tests a condition — a weekday, a minimum, a maximum — so a rule
     * carrying none of them matched nothing at all, silently.
     *
     * The park had written six of these: "Geen winterverhuur zomerlodge",
     * "…trekkershut", "…kamperen", for two winters each. Every one was inert.
     * The operator believed three accommodations were shut from October to
     * March and the website went on selling them, which is the worst shape a
     * bug can have — the screen agreed with them and the answer did not.
     *
     * Read off the NIGHTS, not off the two ends. That is a deliberate
     * departure from the ARI convention the rest of this function follows, and
     * it is what a closure means: "we are shut" is a statement about the days,
     * so any stay using one of them is refused, however it arrives or leaves.
     * A channel expresses the same thing by closing each night individually.
     *
     * `departure > r.from` for the reason spelled out below: a stay ending ON
     * the first day of the window slept none of its nights.
     */
    if (r.arriveOn === undefined && r.departOn === undefined
      && r.minNights == null && r.maxNights == null) {
      if (from < r.until && departure > r.from) return 'closed';
      continue;
    }

    // Arrival-side rules are read off the arrival date alone.
    if (from >= r.from && from < r.until) {
      if (r.arriveOn && !r.arriveOn.includes(weekdayOf(from))) return 'closed-to-arrival';
      if (r.minNights != null && nights < r.minNights) return 'too-short';
      if (r.maxNights != null && nights > r.maxNights) return 'too-long';
    }
    // ...and departure-side rules off the departure date alone.
    //
    // `>` and not `>=`. A stay ending ON the first day of the window used none
    // of its nights: a guest arriving the Saturday before Ascension and leaving
    // Wednesday morning slept only low-season nights, and refusing them a
    // departure protects a weekend they were never in.
    //
    // The park hit this on its own rules. "Arrive Wednesday, leave Monday" over
    // 5-10 May 2027 was written to stop somebody breaking into the middle of
    // the long weekend, and it also turned away every short stay that ended as
    // the weekend began.
    if (departure > r.from && departure < r.until) {
      if (r.departOn && !r.departOn.includes(weekdayOf(departure))) return 'closed-to-departure';
    }
  }
  return null;
}

/**
 * Widen periods onto the boundaries a stay could actually use.
 *
 * When only some days accept an arrival or a departure, a window may be
 * stretched outward to the nearest usable boundary without changing which
 * LEGAL stays it blocks: a stay beginning at or before the window already
 * begins at or before the previous allowed arrival, and symmetrically at the
 * far end. Widening therefore costs nothing and lets neighbouring windows
 * collapse into one another.
 *
 * The saving is real on a card with Saturday-only changeovers, where six days
 * in seven can never start a stay and every window rounds onto the same weekly
 * grid.
 */
export function snapToBoundaries(
  p: Periods, arrivals: (day: number) => boolean, departures: (day: number) => boolean,
  horizon: { from: number; until: number },
): Periods {
  const back = (d: number) => {
    for (let x = d; x >= horizon.from; x--) if (arrivals(x)) return x;
    return horizon.from;
  };
  const forward = (d: number) => {
    for (let x = d; x <= horizon.until; x++) if (departures(x)) return x;
    return horizon.until;
  };
  const snap = (w: Window) => ({ from: back(w.from), until: forward(w.until) });

  const closed = p.closed.map(snap);
  const switching = p.switching.map(snap);

  // Merge and drop what the widening made redundant, in the order the rules
  // require: a closure absorbs anything it touches, because it blocks on
  // overlap and therefore blocks strictly more than a switching window over
  // the same dates.
  const mergedClosed: Window[] = [];
  for (const w of [...closed].sort((a, b) => a.from - b.from || a.until - b.until)) {
    const last = mergedClosed[mergedClosed.length - 1];
    if (last && w.from <= last.until) last.until = Math.max(last.until, w.until);
    else mergedClosed.push({ ...w });
  }

  const keptSwitching: Window[] = [];
  for (const w of [...switching].sort((a, b) => a.from - b.from || a.until - b.until)) {
    // Redundant against any closure it MEETS, not merely one it contains.
    //
    // If the window overlaps a closure at all, a stay containing the window
    // also contains the overlapping part, and therefore overlaps the closure —
    // which already refuses it. Testing containment alone was not enough:
    // widening can push a window onto the edge of a closure without swallowing
    // it, and the pair then breaks the rule that the two never meet.
    if (mergedClosed.some((c) => w.from < c.until && w.until > c.from)) continue;
    // Redundant against another switching window it contains.
    if (keptSwitching.some((k) => w.from <= k.from && w.until >= k.until)) continue;
    // ...and evict any already-kept window that this one is contained by.
    for (let i = keptSwitching.length - 1; i >= 0; i--) {
      const k = keptSwitching[i];
      if (k.from <= w.from && k.until >= w.until) keptSwitching.splice(i, 1);
    }
    keptSwitching.push({ ...w });
  }

  return { closed: mergedClosed, switching: keptSwitching };
}

/**
 * The four rules, checked rather than assumed.
 *
 * Stated as an assertion because they are consequences of how the periods are
 * built, and a change that quietly breaks one of them would not otherwise
 * announce itself — it would just start refusing bookings the park can take.
 */
export function periodsWellFormed(p: Periods): string | null {
  for (const a of p.closed) {
    for (const b of p.closed) {
      if (a === b) continue;
      if (a.from < b.until && a.until > b.from) return 'two closures overlap';
    }
  }
  for (const s of p.switching) {
    for (const c of p.closed) {
      if (s.from < c.until && s.until > c.from) return 'a switching window meets a closure';
    }
    for (const t of p.switching) {
      if (s === t) continue;
      if (s.from <= t.from && s.until >= t.until) return 'a switching window contains another';
    }
  }
  return null;
}
